import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount'
import moment from '../../utilities/Moment'
import NumberFormatter from '../../utilities/NumberFormatter'
import OgmHelper from '../../helpers/OgmHelper'
import { TransactionsController } from '../../controllers/accountant'
import { AccountantApiError } from '../../controllers/accountant/request'
import { AccountantWorkspace, RemittanceInformationType, Transaction } from '../../types'
import ExportTransactionsModal from '../../components/Modals/Accountant/ExportTransactionsModal'
import { cn } from '../../lib/utils'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination'
import { CheckIcon, DownloadIcon, LandmarkIcon, XIcon } from 'lucide-react'

interface IProps {
  workspace: AccountantWorkspace
  onNotFound: () => void
}

const formatRemittanceInformation = (transaction: Transaction) => {
  if (transaction.remittance_information_type === RemittanceInformationType.STRUCTURED && OgmHelper.validate(transaction.remittance_information)) {
    return OgmHelper.format(transaction.remittance_information)
  }

  return transaction.remittance_information || '-'
}

export default function Transactions(props: IProps) {
  const { workspace: { setting } } = props
  const { t } = useTranslation()

  const [transactions, setTransactions] = React.useState<Transaction[]>([])
  const [currentPage, setCurrentPage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(1)
  const [didInitialLoad, setDidInitialLoad] = React.useState(false)
  const [showExportTransactionsModal, setShowExportTransactionsModal] = React.useState(false)

  // Ignore responses that arrive after the workspace was switched
  const activeWorkspaceId = React.useRef(props.workspace.id)
  activeWorkspaceId.current = props.workspace.id

  const fetchTransactions = (page = 1) => {
    const requestedWorkspaceId = props.workspace.id

    TransactionsController
      .getTransactions(props.workspace.id, { page: page })
      .then(response => {
        if (activeWorkspaceId.current !== requestedWorkspaceId) return

        const { transactions: newTransactions, current_page, total_pages } = response

        setTransactions(newTransactions)
        setCurrentPage(current_page)
        setTotalPages(total_pages)
        setDidInitialLoad(true)
      })
      .catch(error => {
        if (activeWorkspaceId.current !== requestedWorkspaceId) return
        if (error instanceof AccountantApiError && error.status === 404) props.onNotFound()
        else console.error(error)
      })
  }

  React.useEffect(() => {
    setDidInitialLoad(false)
    fetchTransactions(1)
  }, [props.workspace.id])

  return (
    <>
      <Helmet>
        <title>{t('Accountant::Bizzey | Transactions')}</title>
      </Helmet>
      <ScrollToTopOnMount />

      <div className="flex items-center justify-end mb-4">
        <Button onClick={() => setShowExportTransactionsModal(true)}>
          <DownloadIcon />
          {t('Accountant::Export transactions')}
        </Button>
      </div>

      {!didInitialLoad && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      )}

      {didInitialLoad && transactions.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border py-24 text-center">
          <LandmarkIcon className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('Accountant::No transactions found')}</p>
        </div>
      )}

      {didInitialLoad && transactions.length > 0 && (
        <div className="rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead>{t('Transactions::Account')}</TableHead>
                <TableHead>{t('Transactions::Counterpart name')}</TableHead>
                <TableHead>{t('Transactions::Counterpart account number')}</TableHead>
                <TableHead>{t('Transactions::Remittance information')}</TableHead>
                <TableHead className="text-right">{t('Transactions::Execution date')}</TableHead>
                <TableHead className="text-right">{t('Transactions::Amount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell className="w-10">
                    {transaction.verified
                      ? <CheckIcon className="size-4 text-green-600" aria-label={t('Transactions::Verified')} />
                      : <XIcon className="size-4 text-destructive" />}
                  </TableCell>
                  <TableCell className="max-w-37.5 truncate">
                    {transaction.account?.name || '-'}
                  </TableCell>
                  <TableCell className="max-w-37.5 truncate">
                    {transaction.counterpart_name || '-'}
                  </TableCell>
                  <TableCell>
                    {transaction.counterpart_account_number || '-'}
                  </TableCell>
                  <TableCell className="max-w-37.5 truncate">
                    {formatRemittanceInformation(transaction)}
                  </TableCell>
                  <TableCell className="text-right">
                    {moment(transaction.execution_time).format(setting.date_format)}
                  </TableCell>
                  <TableCell className={cn('text-right font-medium', Number(transaction.amount) > 0 ? 'text-green-600' : 'text-destructive')}>
                    {NumberFormatter.formatCurrency(transaction.currency, setting.number_format, transaction.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {didInitialLoad && totalPages > 1 && (
        <Pagination className="mt-4 justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  currentPage > 1 && fetchTransactions(currentPage - 1)
                }}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }).map((_, index) => (
              <PaginationItem key={index}>
                <PaginationLink
                  href="#"
                  isActive={currentPage === index + 1}
                  onClick={(e) => {
                    e.preventDefault()
                    fetchTransactions(index + 1)
                  }}
                >
                  {index + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  currentPage < totalPages && fetchTransactions(currentPage + 1)
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <ExportTransactionsModal
        workspaceId={props.workspace.id}
        open={showExportTransactionsModal}
        onOpenChange={setShowExportTransactionsModal}
      />
    </>
  )
}
